import React from 'react';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import { IrlEventTrackingEvent } from 'src/gql/graphql';
import { Card } from './card';
import { ContentHeader, Heading, Description, ShopNowButton, Content } from './styles';
import { IrlEventLoading } from './irl-event-loading';
import { IrlEventError } from './irl-event-error';
import { RewardCard } from './reward-card';
import { useIrlEventReward } from '../hooks/use-irl-event-reward';
import { useIrlEventTracking } from '../hooks/use-irl-event-tracking';
import { formatExpirationDate } from '../utils/format-expiration-date';
import type { LoyaltyUserType } from '../types';

type IrlEventSignupEndProps = {
  hasIncentive: boolean;
  hasLoyaltyProgram: boolean;
  isEligibleForReward: boolean;
  loyaltyUserType: LoyaltyUserType;
  optInRequired: boolean;
  shopNowUrl: string | null;
};

const DEFAULT_TITLE = 'Thank you for attending';
const DEFAULT_DESCRIPTION = 'It was great having you at our event — explore our shop below.';

export function IrlEventSignupEnd({
  hasIncentive,
  hasLoyaltyProgram,
  isEligibleForReward,
  loyaltyUserType,
  optInRequired,
  shopNowUrl: defaultShopNowUrl,
}: IrlEventSignupEndProps): JSX.Element {
  const { trackEvent } = useIrlEventTracking();
  const { reward, loading, error, showReward } = useIrlEventReward({
    hasIncentive,
    hasLoyaltyProgram,
    isEligibleForReward,
    loyaltyUserType,
    optInRequired,
  });

  const isNonIncentiveEvent = reward?.rewardType === 'none';
  const showCustomContent = showReward || isNonIncentiveEvent;

  const title = showCustomContent && reward?.title ? reward.title : DEFAULT_TITLE;
  const description = showCustomContent && reward?.description ? reward.description : DEFAULT_DESCRIPTION;
  const rewardExpiration = showReward && reward.expiry ? formatExpirationDate(reward.expiry) : 'Never expires';
  const shopNowUrl = reward?.shopNowUrl ?? defaultShopNowUrl;

  const handleShopNowClick = (): void => {
    void trackEvent(IrlEventTrackingEvent.shopNowClicked);
  };

  if (loading) {
    return <IrlEventLoading />;
  }

  if (error) {
    return (
      <IrlEventError
        title='Something went wrong'
        description="We're having trouble loading your reward right now. Please try again later."
      />
    );
  }

  return (
    <>
      <MetaTags title='Success' description='Signup complete' />

      <Card data-testid='irl-event-signup-end'>
        <ContentHeader>
          <StyledHeading>{title}</StyledHeading>
          <Description>{description}</Description>
        </ContentHeader>

        <Content narrow>
          {showReward && (
            <RewardCard
              rewardType={reward.rewardType}
              rewardName={reward.rewardName ?? ''}
              rewardExpirationDate={rewardExpiration}
              rewardImageUrl={reward.rewardImageUrl ?? ''}
            />
          )}

          {shopNowUrl && (
            <ShopNowButton
              href={shopNowUrl}
              target='_blank'
              rel='noopener noreferrer'
              variant='primary'
              size='large'
              onClick={handleShopNowClick}
              data-testid='irl-event-signup-end-shop-now-button'
            >
              Shop Now
            </ShopNowButton>
          )}

          {showReward && <RestrictionsText>Restrictions may apply </RestrictionsText>}
        </Content>
      </Card>
    </>
  );
}

const StyledHeading = styled(Heading)`
  margin-top: 10px;
`;

const RestrictionsText = styled.p`
  color: #646d72;
  font-size: 13px;
  text-align: center;
  margin-top: 24px;
`;
